import { IOSMResponse, getV2ValueWP } from '@eventr/common'
import {
    ColoursObj,
    Icon,
    Typography,
    getContrastYIQ,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useCallback } from 'react'
import { openMobileMapsLink } from '../../../../../utils'

/**
 * The address row button of the selected itinerary. Returns null if address information unavailable.
 * @returns A button displaying the address of the itinerary
 */
export const AddressRow = ({ OSMAddress }: { OSMAddress: IOSMResponse }) => {
    /**
     * Collects values for given keys, removing duplicates and empty values.
     * @param keys The keys to collect values for.
     * @returns The values for the specified keys.
     */
    function getAddressComponents(keys: string[]): string[] | undefined {
        return Array.from(
            new Set(
                keys
                    .map((key) => {
                        const value: string = getV2ValueWP(
                            `OSMAddress.address.${key}`,
                            {
                                OSMAddress: OSMAddress,
                            }
                        ).trim()

                        return value
                    })
                    .filter(
                        (value: string) =>
                            value !== undefined &&
                            value !== null &&
                            value !== ''
                    )
            )
        )
    }

    // Collects the address components and joins them into a single string to be displayed.
    const address: () => string | undefined = useCallback(() => {
        const components = getAddressComponents(ADDRESS_DISPLAY_KEYS)

        if (!components) return undefined

        return components.join(', ')
    }, [OSMAddress])

    // Action to open platform-specific maps application/website with the address.
    const action: () => void = useCallback(() => {
        const components = getAddressComponents(ADDRESS_LINK_KEYS)

        if (!components) return

        // Builds link with the address components.
        const query = components.join('+')

        // Ensures the query is not empty.
        if (query === '') return

        // Opens the maps application/website based on the platform.
        openMobileMapsLink(query)
    }, [OSMAddress])

    // Ensures the address is not empty.
    if (address() === '') return null

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            width="100%"
            onClick={(event) => {
                event.stopPropagation()

                action()
            }}
        >
            <Stack direction="row" overflow="hidden" gap={'8px'}>
                <Icon
                    name={'Location_Pin'}
                    iconColour={ColoursObj.coreBrights.primary}
                />

                <Typography
                    id="headerLabels"
                    variant="bodyRegular"
                    color={getContrastYIQ(ColoursObj.backgrounds.secondary)}
                    textOverflow="ellipsis"
                    align="left"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    style={{
                        textDecoration: 'underline',
                        textUnderlineOffset: '3px',
                    }}
                >
                    {address()}
                </Typography>
            </Stack>
        </Stack>
    )
}

/**
 * Keys to use when displaying the address.
 */
const ADDRESS_DISPLAY_KEYS = [
    'house_number',
    'tourism',
    'amenity',
    'road',
    'village',
]

/**
 * Keys to use when encoding the address in an URL.
 */
const ADDRESS_LINK_KEYS = [
    'house_number',
    'tourism',
    'amenity',
    'road',
    'village',
    'town',
    'city',
    'country',
    'postcode',
]
